/**
 * endLocalSession — what "this tab's session ended" means for the chat
 * (W0.3, SCRUM-276) and for guest chat (W2.6).
 *
 * Logout cleared the token and the query cache but not the chat store, so on
 * a shared device the previous person's open thread survived in the tab
 * (finding #8, 2026-10-07). Called by `logout()` (explicit + idle logout),
 * account deletion (SettingsScreen, PrivacyScreen), the admin top bar's log
 * out and, through `ApiClientConfig.onSessionEnded`, both persistent-401
 * paths — for a MEMBER token only (W2.6, W2.1 carry: a request that carried
 * no token ended no session, see api-client.ts / useChatStream.ts).
 *
 * W2.6 (spec edge case "Logout / idle logout / persistent 401"): the guest
 * side goes too — the pointer, the unsent draft, the guest token, a pending
 * "save the conversation?" decision (and a claim still on the wire), and the
 * server-side guest session (discard). Only when this browser holds any of
 * it: discard is a network call, and its local half runs synchronously first.
 *
 * NEVER throws (ruling P7b): every caller runs its redirect or navigate right
 * after this, and a throw — Storage refusing access in private mode, a store
 * subscriber failing — would skip it and reject `logout()`. Each step is
 * best-effort on its own, so one failing step cannot stop the next.
 */
import { useChatStore } from "@/features/chat/chat-store";
import { discardGuestSession } from "@/features/chat/guest/api";
import { resetGuestClaim } from "@/features/chat/guest/GuestClaimController";
import { readGuestDraft, readGuestPointer } from "@/features/chat/guest/pointer";
import { useAuthStore } from "@/lib/auth-store";

function bestEffort(step: () => void): void {
  try {
    step();
  } catch {
    // See the header: the session end must reach its caller's redirect.
  }
}

export function endLocalSession(): void {
  bestEffort(() => useChatStore.getState().resetAll());
  bestEffort(resetGuestClaim);
  bestEffort(() => {
    const hasGuestState =
      useAuthStore.getState().guestToken !== null ||
      readGuestPointer() !== null ||
      readGuestDraft() !== null;
    if (hasGuestState) void discardGuestSession().catch(() => undefined);
  });
}
